// OEM PRICE REQUESTS, THE ORDERING GATE AND OEM ORDERS on the quote screen --
// Order Management O3-O5 (owner decisions D181, 2026-10-30).
//
// ⚠️ THE PANEL IS NOT THE GATE. The server's order_gate decides; this panel
// ASKS it (oem_order_check, which writes nothing) and lists every refusal it
// gives, each naming its line. Placing an order goes through the same gate
// again. Scoping is the server's too: a caller who may not see this quote's
// OEM records gets no panel at all.
//
// O6 / O7: each order shows its DERIVED status (the server's), late / at-risk
// badges, what has been recorded on it, and -- for the owner or an Admin -- a
// form to record the next event. The order status by line sits above the list.
//
// ux-guided-deal: the same reads, split into three blocks the quote screen
// places in journey order (requests under "Get OEM prices", ordering under
// "Order from the OEM", the orders under "Track the OEM order"), fed by ONE
// hook so the progress bar reads exactly what the blocks show.
// OemOrdersPanel (all three, one hook) stays for anything that wants them together.
//
// ⚠️ NOTHING HERE PRICES. An order's price is the OEM's confirmed price,
// copied and frozen by the server; the quote's figures never change.

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  getOemConfirmations, getOemRequests, createOemRequests, updateOemRequest,
  checkOemOrder, createOemOrder, getOemOrders, getWarehouses, getOemOffers,
  type OemOffersView, type OemConfirmationsView, type OemRequest, type OemOrder, type GateRefusal, type OemShipTo,
} from "@/services/api";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { Pill, money } from "@/components/OemConfirmationsPanel";
import { OemOrderDocument } from "@/components/OemOrderDocument";
import { FlagBadges, OrderEvents, OrderProgressLine, OrderStatusPanel, StatusPill } from "@/components/OemOrderStatus";
import { CustomerDatesPanel, CustomerFlagBadges } from "@/components/CustomerDates";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const BOX: React.CSSProperties = {
  marginTop: spacing[12], border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface,
};
const HEAD: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: spacing[10], flexWrap: "wrap",
  padding: `${spacing[10]}px ${spacing[12]}px`, borderBottom: `1px solid ${semanticColors.border}`,
};
const TITLE: React.CSSProperties = { fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary };
const TD: React.CSSProperties = { padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.sm, borderBottom: `1px solid ${semanticColors.border}`, verticalAlign: "top" };
const BTN: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[10]}px`, backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.sm, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = { ...BTN, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none" };
const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, minWidth: 0,
};
const HELP: React.CSSProperties = { fontSize: typography.fontSize.xs, color: semanticColors.textMuted };
const REQ_TONE = { draft: "gray", sent: "amber", answered: "green", cancelled: "gray" } as const;
/** The request's state in plain words (the server's value is kept in data-state). */
export const REQ_LABEL: Record<OemRequest["display_state"], string> = {
  draft: "Not sent yet", sent: "Sent: waiting for the OEM", answered: "Answered", cancelled: "Cancelled",
};

interface Orderable {
  confirmationId: number; lineNo: number; part: string; oem: string;
  price: number; currency: string; remaining: number;
}

/** Active confirmations with something left to order, grouped by OEM. */
export function orderableByOem(view: OemConfirmationsView | null): Record<string, Orderable[]> {
  const out: Record<string, Orderable[]> = {};
  for (const line of view?.lines ?? []) {
    for (const c of line.confirmations) {
      if (c.state !== "active") continue;
      const remaining = c.confirmed_qty - (c.ordered_qty ?? 0);
      if (remaining <= 1e-9) continue;
      const oem = c.oem || line.oem;            // M2: the confirming OEM
      (out[oem] ??= []).push({
        confirmationId: c.id, lineNo: line.line_no, part: line.part_number, oem,
        price: c.confirmed_unit_price, currency: c.currency, remaining,
      });
    }
  }
  return out;
}

// ---- THE ONE READ ------------------------------------------------------------

export interface OemDealData {
  view: OemConfirmationsView | null;
  /** M2 (D197): every OEM offer per line (read-only); null = not visible / not loaded. */
  offers: OemOffersView | null;
  requests: OemRequest[];
  orders: OemOrder[];
  nextNumber: string | null;
  warehouses: { id: number; label: string }[];
  /** The caller may not see this quote's OEM records: no blocks at all. */
  hidden: boolean;
  /** Loaded at least once. */
  loaded: boolean;
  error: string;
  setError: (e: string) => void;
  tick: number;
  refresh: () => void;
}

/** The same three reads the panel always made, once, for every block. */
export function useOemDealData(quoteId: number | undefined, reloadKey = 0): OemDealData {
  const [view, setView] = useState<OemConfirmationsView | null>(null);
  const [offers, setOffers] = useState<OemOffersView | null>(null);
  const [requests, setRequests] = useState<OemRequest[]>([]);
  const [orders, setOrders] = useState<OemOrder[]>([]);
  const [nextNumber, setNextNumber] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<{ id: number; label: string }[]>([]);
  const [hidden, setHidden] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    if (quoteId === undefined) return;
    const [c, r, o] = await Promise.all([getOemConfirmations(quoteId), getOemRequests(quoteId), getOemOrders(quoteId)]);
    if (c.notFound || r.notFound || o.notFound) { setHidden(true); setLoaded(true); return; }
    if (!c.ok || !r.ok || !o.ok) { setError(c.error || r.error || o.error || "Could not read OEM orders."); return; }
    setHidden(false); setError(""); setLoaded(true);
    setView(c.view!); setRequests(r.requests!); setOrders(o.orders!); setNextNumber(o.nextNumber ?? null);
    // M2: the offers are a convenience read -- a failure leaves them absent, never guessed.
    const f = await getOemOffers(quoteId);
    setOffers(f.ok ? f.view ?? null : null);
  }, [quoteId]);

  useEffect(() => { void load(); }, [load, reloadKey, tick]);
  useEffect(() => {
    if (quoteId === undefined) return;
    void (async () => {
      const w = await getWarehouses();
      if (w.ok) setWarehouses((w.warehouses ?? []).filter((x) => x.active !== false && !x.is_ftwz)
        .map((x) => ({ id: x.id, label: `${x.city} (${x.code})` })));
    })();
  }, [quoteId]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { view, offers, requests, orders, nextNumber, warehouses, hidden, loaded, error, setError, tick, refresh };
}

// ---- THE ACTIONS (the blocks' buttons and the next-step card use the same) -----

/** "Request prices": one request per OEM for the unconfirmed lines. Nothing is sent. */
export async function requestOemPrices(quoteId: number): Promise<string | null> {
  const res = await createOemRequests(quoteId);
  return res.ok ? null : (res.error || "No request was made.");
}

/** "Mark sent": the user sent it OUTSIDE Anixify; the contact is optional. null = cancelled prompt. */
export async function markOemRequestSent(r: OemRequest): Promise<string | null | undefined> {
  const contact = window.prompt(`Who at ${r.oem} was asked? (optional)`);
  if (contact === null) return undefined;
  const res = await updateOemRequest(r.id, "sent", { oemContact: contact || undefined });
  return res.ok ? null : (res.error || "Not updated.");
}

// ---- BLOCK 1: PRICE REQUESTS ------------------------------------------------------

export function OemRequestsBlock({ quoteId, data }: { quoteId: number; data: OemDealData }) {
  const { view, requests, refresh, setError } = data;
  if (data.hidden) return null;
  const canRecord = !!view?.can_record;

  const request = async () => {
    const err = await requestOemPrices(quoteId);
    if (err) setError(err); else refresh();
  };
  const markSent = async (r: OemRequest) => {
    const err = await markOemRequestSent(r);
    if (err === undefined) return;
    if (err) setError(err); else refresh();
  };
  const cancel = async (r: OemRequest) => {
    const reason = window.prompt("Why is this request cancelled?");
    if (reason === null) return;
    const res = await updateOemRequest(r.id, "cancel", { reason });
    if (!res.ok) setError(res.error || "Not cancelled."); else refresh();
  };

  return (
    <section data-testid="oem-requests" style={BOX}>
      <div style={HEAD}>
        <span style={TITLE}>OEM price requests</span>
        <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          A record of asking each OEM for its final price and lead time. Nothing is sent from here: you contact the OEM yourself.
        </span>
        {canRecord && (
          <span style={{ marginLeft: "auto", display: "grid", justifyItems: "end", gap: spacing[2] }}>
            <button type="button" data-testid="oemr-create" style={BTN} onClick={() => { void request(); }}>
              Request prices for unconfirmed lines
            </button>
            <span style={HELP}>Creates one request per OEM. Nothing is emailed.</span>
          </span>
        )}
      </div>
      {requests.length === 0 ? (
        <div data-testid="oemr-empty" style={{ padding: spacing[12], fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}>
          No price requests yet. When you ask an OEM to confirm, record it here so everyone can see who is waiting on whom.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} data-testid={`oemr-row-${r.id}`}>
                  <td style={{ ...TD, fontWeight: typography.fontWeight.medium }}>{r.oem}</td>
                  <td style={TD} data-state={r.display_state}>
                    <Pill tone={REQ_TONE[r.display_state]} testId={`oemr-state-${r.id}`}>{REQ_LABEL[r.display_state]}</Pill>
                  </td>
                  <td style={TD}>{r.lines.map((l) => `${l.part_number} × ${l.qty}`).join(", ")}</td>
                  <td style={{ ...TD, color: semanticColors.textSecondary }}>
                    {r.sent_at ? `sent ${formatTimestampDate(r.sent_at)} by ${r.sent_by ?? "—"}${r.oem_contact ? ` to ${r.oem_contact}` : ""}`
                      : `created by ${r.created_by ?? "—"}`}
                    {r.due_by ? ` · answer by ${formatDate(r.due_by)}` : ""}
                    {r.cancel_reason ? ` · cancelled: ${r.cancel_reason}` : ""}
                  </td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>
                    {canRecord && r.state === "draft" && (
                      <span style={{ display: "inline-grid", gap: spacing[2] }}>
                        <button type="button" data-testid={`oemr-sent-${r.id}`} style={BTN} onClick={() => { void markSent(r); }}>Mark sent</button>
                        <span style={HELP}>You sent it outside Anixify; nothing is emailed.</span>
                      </span>
                    )}{" "}
                    {canRecord && r.state !== "cancelled" && r.display_state !== "answered" && <button type="button" data-testid={`oemr-cancel-${r.id}`} style={BTN} onClick={() => { void cancel(r); }}>Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---- BLOCK 2: PLACING AN ORDER --------------------------------------------------------

function RaiseOrder({
  quoteId, oem, rows, warehouses, nextNumber, onPlaced, onRefusals,
}: {
  quoteId: number; oem: string; rows: Orderable[];
  warehouses: { id: number; label: string }[]; nextNumber: string | null; onPlaced: () => void;
  onRefusals?: (oem: string, refusals: GateRefusal[] | null) => void;
}) {
  const [qty, setQty] = useState<Record<number, string>>(
    () => Object.fromEntries(rows.map((r) => [r.confirmationId, String(r.remaining)])));
  const [picked, setPicked] = useState<Record<number, boolean>>(
    () => Object.fromEntries(rows.map((r) => [r.confirmationId, true])));
  const [ship, setShip] = useState<string>("customer");
  const [refusals, setRefusals] = useState<GateRefusal[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // ABSENT IS NOT ZERO: an emptied quantity is sent as it is, and the gate says so.
  const items = useMemo(() => rows.filter((r) => picked[r.confirmationId]).map((r) => ({
    confirmation_id: r.confirmationId,
    qty: qty[r.confirmationId] === "" ? (null as unknown as number) : Number(qty[r.confirmationId]),
  })), [rows, picked, qty]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      if (!items.length) { setRefusals([{ line_no: null, part_number: null, reason: "Choose at least one line." }]); return; }
      const res = await checkOemOrder(quoteId, items);
      if (!alive) return;
      if (!res.ok) { setError(res.error || "The order could not be checked."); setRefusals(null); return; }
      setError(""); setRefusals(res.refusals ?? []);
    })();
    return () => { alive = false; };
  }, [quoteId, items]);
  useEffect(() => { onRefusals?.(oem, refusals); }, [oem, refusals, onRefusals]);
  useEffect(() => () => onRefusals?.(oem, null), [oem, onRefusals]);

  const shipTo = (): OemShipTo => (ship === "customer" ? { kind: "customer" } : { kind: "warehouse", warehouse_id: Number(ship) });

  const place = async () => {
    setBusy(true); setError("");
    const res = await createOemOrder(quoteId, items, shipTo());
    setBusy(false);
    if (!res.ok) { setError(res.error === "refused" ? "The gate refused this order." : (res.error || "Not placed.")); if (res.refusals) setRefusals(res.refusals); return; }
    onPlaced();
  };

  const slug = oem.replace(/[^A-Za-z0-9]/g, "");
  return (
    <div data-testid={`oemo-raise-${slug}`} style={{ padding: spacing[12], borderBottom: `1px solid ${semanticColors.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[10], marginBottom: spacing[8], flexWrap: "wrap" }}>
        <span style={{ fontWeight: typography.fontWeight.medium }}>{oem}</span>
        <label style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, display: "flex", gap: spacing[6], alignItems: "center" }}>
          Ship to
          <select data-testid={`oemo-shipto-${slug}`} style={INPUT} value={ship} onChange={(e) => setShip(e.target.value)}>
            <option value="customer">Customer site (drop-ship)</option>
            {warehouses.map((w) => <option key={w.id} value={String(w.id)}>Our warehouse: {w.label}</option>)}
          </select>
        </label>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["", "#", "Part", "Confirmed price", "Left to order", "Order now"].map((h) => (
            <th key={h} style={{ ...TD, textAlign: "left", color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium }}>{h}</th>))}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.confirmationId}>
                <td style={TD}><input type="checkbox" aria-label={`Order line ${r.lineNo}`} data-testid={`oemo-pick-${r.confirmationId}`} checked={!!picked[r.confirmationId]}
                  onChange={(e) => setPicked((p) => ({ ...p, [r.confirmationId]: e.target.checked }))} /></td>
                <td style={TD}>{r.lineNo}</td>
                <td style={{ ...TD, whiteSpace: "nowrap" }}>{r.part}</td>
                <td style={{ ...TD, whiteSpace: "nowrap" }}>{money(r.price, r.currency)}</td>
                <td style={TD}>{r.remaining}</td>
                <td style={TD}><input aria-label={`Quantity to order now, line ${r.lineNo}`} data-testid={`oemo-qty-${r.confirmationId}`} style={INPUT} inputMode="decimal"
                  value={qty[r.confirmationId] ?? ""} onChange={(e) => setQty((q) => ({ ...q, [r.confirmationId]: e.target.value }))} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {refusals && refusals.length > 0 && (
        <div data-testid={`oemo-refusals-${slug}`} role="alert" style={{
          marginTop: spacing[8], padding: `${spacing[8]}px ${spacing[12]}px`, backgroundColor: colors.red50,
          border: `1px solid ${colors.red100}`, borderRadius: radius.base, color: colors.red600, fontSize: typography.fontSize.sm,
        }}>
          <div style={{ fontWeight: typography.fontWeight.medium }}>What's missing to place this order:</div>
          <ul style={{ margin: `${spacing[4]}px 0 0`, paddingLeft: 0, listStyle: "none" }}>
            {refusals.map((r, i) => <li key={i}><span aria-hidden>✗ </span>{r.reason}</li>)}
          </ul>
        </div>
      )}
      {refusals && refusals.length === 0 && (
        <div data-testid={`oemo-ready-${slug}`} style={{ marginTop: spacing[8], fontSize: typography.fontSize.sm, color: colors.green600 }}>
          <span aria-hidden>✓ </span>Every condition holds. The order will be numbered {nextNumber ?? "from the OEM order sequence"}.
        </div>
      )}
      {error && <div role="alert" style={{ marginTop: spacing[6], color: colors.red600, fontSize: typography.fontSize.sm }}>{error}</div>}
      <div style={{ display: "grid", justifyItems: "end", gap: spacing[2], marginTop: spacing[8] }}>
        <button type="button" data-testid={`oemo-place-${slug}`}
          // A refused order's button must LOOK refused, not just be inert.
          style={busy || !refusals || refusals.length > 0
            ? { ...PRIMARY, backgroundColor: colors.gray300, cursor: "not-allowed" } : PRIMARY}
          disabled={busy || !refusals || refusals.length > 0} onClick={() => { void place(); }}>
          {busy ? "Placing…" : `Place OEM order with ${oem}`}
        </button>
        <span style={HELP}>Takes the next OEM order number; it cannot be undone. Nothing is emailed: print it from Document.</span>
      </div>
    </div>
  );
}

export function OemOrderingBlock({ quoteId, data, reloadKey = 0, onRefusals }: {
  quoteId: number; data: OemDealData; reloadKey?: number;
  onRefusals?: (oem: string, refusals: GateRefusal[] | null) => void;
}) {
  const { view, warehouses, nextNumber, tick, refresh, error } = data;
  if (data.hidden) return null;
  const canRecord = !!view?.can_record;
  const won = view?.status === "accepted";
  const groups = orderableByOem(view);
  return (
    <section data-testid="oem-orders" style={BOX}>
      <div style={HEAD}>
        <span style={TITLE}>OEM orders</span>
        <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          {won ? "Placed only on confirmed, valid OEM details, at the OEM's confirmed price."
            : "An OEM order can be placed once the customer says yes (the quote is Won)."}
        </span>
      </div>
      {error && <div role="alert" style={{ padding: spacing[12], color: colors.red600, fontSize: typography.fontSize.sm }}>{error}</div>}
      {canRecord && won && Object.entries(groups).map(([oem, rows]) => (
        <RaiseOrder key={`${oem}-${tick}`} quoteId={quoteId} oem={oem} rows={rows} warehouses={warehouses}
          nextNumber={nextNumber} onPlaced={refresh} onRefusals={onRefusals} />
      ))}
      {canRecord && won && Object.keys(groups).length === 0 && (
        <div data-testid="oemo-nothing-left" style={{ padding: spacing[12], fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}>
          Nothing confirmed is left to order.
        </div>
      )}
      {/* M3.1 (B3): what we promised the customer, per line, on a Won quote. */}
      {won && <CustomerDatesPanel quoteId={quoteId} canEdit={canRecord} reloadKey={tick + reloadKey} />}
    </section>
  );
}

// ---- BLOCK 3: THE ORDERS AND THEIR EVENTS --------------------------------------------------

export function OemTrackingBlock({ quoteId, data, reloadKey = 0, openOrder, standalone = true }: {
  quoteId: number; data: OemDealData; reloadKey?: number;
  /** Opens this order's events (the next-step card asks for it). */
  openOrder?: { id: number; signal: number } | null;
  /** false = inside OemOrdersPanel's box (no box of its own). */
  standalone?: boolean;
}) {
  const { view, orders, tick, refresh } = data;
  const [doc, setDoc] = useState<number | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => { if (openOrder) setOpen(openOrder.id); }, [openOrder]);
  if (data.hidden) return null;
  const canRecord = !!view?.can_record;
  const body = (
    <>
      {orders.length > 0 && <OrderStatusPanel quoteId={quoteId} reloadKey={tick + reloadKey} />}
      {orders.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Number", "OEM", "Status", "Lines", "Value", "Ship to", "Placed", ""].map((h) => (
              <th key={h} style={{ ...TD, textAlign: "left", color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium }}>{h}</th>))}</tr></thead>
            <tbody>
              {orders.map((o) => (<Fragment key={o.id}>
                <tr data-testid={`oemo-order-${o.id}`}>
                  <td style={{ ...TD, fontWeight: typography.fontWeight.medium, whiteSpace: "nowrap" }}>{o.number}</td>
                  <td style={TD}>{o.oem}</td>
                  <td style={TD}>
                    <span style={{ display: "inline-flex", gap: spacing[4], flexWrap: "wrap" }}>
                      <StatusPill status={o.status} testId={`oemo-status-${o.id}`} />
                      <FlagBadges late={o.late} atRisk={o.at_risk} reasons={o.flag_reasons} testId={`oemo-flags-${o.id}`} />
                      <CustomerFlagBadges promiseAfter={o.promise_after_customer} atRisk={o.customer_date_at_risk}
                        reasons={o.customer_reasons} testId={`oemo-cflags-${o.id}`} />
                    </span>
                    <div style={{ marginTop: spacing[4] }}><OrderProgressLine order={o} testId={`oemo-row-progress-${o.id}`} /></div>
                  </td>
                  <td style={TD}>{o.lines.map((l) => `${l.part_number} × ${l.qty_ordered}`).join(", ")}</td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>{money(o.lines.reduce((s, l) => s + l.qty_ordered * l.unit_price, 0), o.currency)}</td>
                  <td style={TD}>{o.ship_to_kind === "customer" ? "Customer site" : "Our warehouse"}</td>
                  <td style={{ ...TD, color: semanticColors.textSecondary }}>{o.placed_at ? formatTimestampDate(o.placed_at) : "—"} · {o.placed_by ?? "—"}</td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>
                    <button type="button" data-testid={`oemo-open-${o.id}`} aria-expanded={open === o.id} style={BTN}
                      onClick={() => setOpen(open === o.id ? null : o.id)}>{open === o.id ? "Hide" : "Events"}</button>{" "}
                    <button type="button" data-testid={`oemo-doc-${o.id}`} style={BTN} onClick={() => setDoc(o.id)}>Document</button>
                  </td>
                </tr>
                {open === o.id && (
                  <tr><td colSpan={8} style={{ padding: 0, borderBottom: `1px solid ${semanticColors.border}` }}>
                    <OrderEvents order={o} canRecord={canRecord} onRecorded={refresh} />
                  </td></tr>
                )}
              </Fragment>))}
            </tbody>
          </table>
        </div>
      )}
      {doc !== null && <OemOrderDocument orderId={doc} onClose={() => setDoc(null)} />}
    </>
  );
  if (!standalone) return body;
  return (
    <section data-testid="oem-orders-tracking" style={BOX}>
      <div style={HEAD}>
        <span style={TITLE}>Placed OEM orders</span>
        <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          Record what the OEM tells you: acknowledged, shipped, delivered. Status is worked out from those entries.
        </span>
      </div>
      {orders.length === 0 && (
        <div data-testid="oemo-none" style={{ padding: spacing[12], fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}>
          No OEM order on this quote yet.
        </div>
      )}
      {body}
    </section>
  );
}

/** All three blocks on one hook (the original panel's shape). */
export function OemOrdersPanel({ quoteId, reloadKey = 0 }: { quoteId: number; reloadKey?: number }) {
  const data = useOemDealData(quoteId, reloadKey);
  if (data.hidden) return null;
  return (
    <>
      <OemRequestsBlock quoteId={quoteId} data={data} />
      <OemOrderingBlock quoteId={quoteId} data={data} reloadKey={reloadKey} />
      <OemTrackingBlock quoteId={quoteId} data={data} reloadKey={reloadKey} />
    </>
  );
}
